import React, { useState, useEffect, useMemo } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useFilters,
  useGlobalFilter,
} from "react-table";
import { useBuildingAggregates } from "./hooks/useBuildingAggregates";
import AlertTrendsCard from "./AlertTrendsCard";
import SortIndicator from "../../components/ui/SortIndicator";
import { IconContext } from "react-icons";
import { PiBuildingsFill, PiBroadcastFill } from "react-icons/pi";
import { IoNotifications, IoCloudDone } from "react-icons/io5";
import { LuPowerOff } from "react-icons/lu";
import "./tableStyle.scss";

function alertCountColor(v) {
  if (v <= 0) return "text-ink-3";
  return "";
}

function alertCountStyle(v) {
  if (v <= 0) return undefined;
  if (v < 2) return { color: "#F1C40F" }; // yellow
  if (v < 5) return { color: "#FB8C00" }; // orange
  if (v < 10) return { color: "#E74C3C" }; // red
  return { color: "#B00020" }; // deep red
}

function AggregateCell({ value, status, tone }) {
  if (status === "loading") {
    return <span className="text-ink-3" aria-label="Loading">…</span>;
  }
  if (status === "error" || value == null) {
    return (
      <span
        className="text-ink-3"
        title="Could not load this value"
        aria-label="Unavailable"
      >
        —
      </span>
    );
  }
  if (tone === "fire") {
    return (
      <span
        className={`font-semibold ${alertCountColor(value)}`}
        style={alertCountStyle(value)}
      >
        {value}
      </span>
    );
  }
  // Other tones: simple binary color (zero muted, non-zero accented).
  const colorClass =
    value > 0
      ? tone === "info"
        ? "text-alert-info font-semibold"
        : ""
      : tone
        ? "text-ink-3"
        : "";
  return <span className={colorClass}>{value}</span>;
}

export default function BuildingEventsTable(props) {
  const [activeBuildingId, setActiveBuildingId] = useState(null);

  const handleMouseEnter = (row) => {
    setActiveBuildingId(row.buildingId);
  };
  const handleRowClick = (row) => props.loadUnitsData(row.buildingId);

  // Per-building Units + Alerts aggregates (Alerts respects the date range).
  const { aggregates, allAlertRecords, totals } = useBuildingAggregates(
    props.data,
    props.startDate,
    props.endDate
  );

  // Merge aggregates into the row data so react-table can sort on them.
  const tableData = useMemo(
    () =>
      (props.data || []).map((b) => {
        const a = aggregates.get(b.buildingId);
        return {
          ...b,
          unitsCount: a?.unitsCount ?? null,
          alertsCount: a?.alertsCount ?? null,
          shutdownsCount: a?.shutdownsCount ?? null,
          hasPCO: a?.hasPCO ?? false,
          aggStatus: a?.aggStatus ?? "loading",
        };
      }),
    [props.data, aggregates]
  );

  const sortName = (rowA, rowB) =>
    rowA.original.name.localeCompare(rowB.original.name, undefined, {
      numeric: true,
    });

  // Treat unloaded values as -Infinity so they cluster together rather than
  // jumping around as queries resolve. Stable sort keeps ties in place.
  const numericSort = (key) => (rowA, rowB) => {
    const a =
      rowA.original[key] == null ? Number.NEGATIVE_INFINITY : rowA.original[key];
    const b =
      rowB.original[key] == null ? Number.NEGATIVE_INFINITY : rowB.original[key];
    if (a === b) return 0;
    return a > b ? 1 : -1;
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Address",
        accessor: "name",
        sortType: sortName,
        filter: "fuzzyText",
        isVisible: true,
      },
      {
        Header: "Units",
        accessor: "unitsCount",
        sortType: numericSort("unitsCount"),
        Cell: ({ row }) => (
          <AggregateCell
            value={row.original.unitsCount}
            status={
              row.original.aggStatus === "error" &&
              row.original.unitsCount != null
                ? "ready"
                : row.original.aggStatus
            }
          />
        ),
        isVisible: true,
      },
      {
        Header: "Alerts",
        accessor: "alertsCount",
        sortType: numericSort("alertsCount"),
        Cell: ({ row }) => (
          <AggregateCell
            value={row.original.alertsCount}
            status={row.original.aggStatus}
            tone="fire"
          />
        ),
        isVisible: true,
      },
      {
        Header: "Shutdowns",
        accessor: "shutdownsCount",
        sortType: numericSort("shutdownsCount"),
        Cell: ({ row }) => {
          if (
            row.original.aggStatus === "ready" &&
            !row.original.hasPCO
          ) {
            return (
              <span
                className="text-ink-3 font-semibold"
                title="No sensors at this property have a power cutoff unit attached."
              >
                N/A
              </span>
            );
          }
          return (
            <AggregateCell
              value={row.original.shutdownsCount}
              status={row.original.aggStatus}
              tone="info"
            />
          );
        },
        isVisible: true,
      },
    ],
    []
  );

  const filterTypes = React.useMemo(
    () => ({
      text: (rows, id, filterValue) =>
        rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        }),
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: tableData,
      filterTypes,
      initialState: {
        hiddenColumns: columns
          .filter((column) => !column.isVisible)
          .map((column) => column.accessor),
        sortBy: [{ id: "name", desc: false }],
        pageIndex: 0,
        pageSize: 10,
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // Apply the search query coming from the page header.
  useEffect(() => {
    setGlobalFilter(props.searchQuery || undefined);
  }, [props.searchQuery, setGlobalFilter]);

  // Pad the page out to a fixed row count so the table height never collapses
  // as the user filters. Empty rows render as transparent placeholders.
  const paddedPage = useMemo(() => {
    const padded = [...page];
    while (padded.length < pageSize) padded.push(null);
    return padded;
  }, [page, pageSize]);

  useEffect(() => {
    if (props.data && props.data.length > 0) {
      setActiveBuildingId(props.data[0].buildingId);
    } else {
      setActiveBuildingId(null);
    }
  }, [props.data]);

  return (
    <div className="mt-2">
      <h1 className="text-2xl font-semibold text-white mb-5">
        {props.organization ? `${props.organization} Properties` : "Properties"}
      </h1>

      {props.data.length === 0 ? (
        <div className="rounded-card border border-dashed border-white/25 p-10 text-center bg-white/5">
          <h2 className="text-2xl font-semibold text-white">
            No site, property, or building was found
          </h2>
          <p className="mt-3 text-ink-3">
            Install at least one CTS smart cooking sensor with the phone app
            and reload this page.
          </p>
        </div>
      ) : (
        <>
          <div className="container-infobox">
            <div className="info-textbox">
              <IconContext.Provider
                value={{ color: "#BBDA62", className: "icon" }}
              >
                <PiBuildingsFill className="icon" />
              </IconContext.Provider>
              <div className="info-textbox-body">
                <span>{totals.properties}</span>
                <h2>Properties</h2>
              </div>
            </div>
            <div className="info-textbox">
              <IconContext.Provider
                value={{ color: "#A4A4A4", className: "icon" }}
              >
                <PiBroadcastFill className="icon" />
              </IconContext.Provider>
              <div className="info-textbox-body">
                <span>{totals.units}</span>
                <h2>Sensors</h2>
              </div>
            </div>
            <div className="info-textbox">
              <IconContext.Provider
                value={{ color: "#0D47A1", className: "icon" }}
              >
                <IoCloudDone className="icon" />
              </IconContext.Provider>
              <div className="info-textbox-body">
                <span>
                  {totals.onlineSensors} / {totals.units}
                </span>
                <h2>Sensors online</h2>
              </div>
            </div>
            <div className="info-textbox">
              <IconContext.Provider
                value={{ color: "#E74C3C", className: "icon" }}
              >
                <IoNotifications className="icon" />
              </IconContext.Provider>
              <div className="info-textbox-body">
                <span>{totals.alerts}</span>
                <h2>Alerts</h2>
              </div>
            </div>
            <div className="info-textbox">
              <IconContext.Provider
                value={{ color: "#1976D2", className: "icon" }}
              >
                <LuPowerOff className="icon" />
              </IconContext.Provider>
              <div className="info-textbox-body">
                <span>
                  {totals.pcoEquipped > 0 ? totals.shutdowns : "N/A"}
                </span>
                <h2>Shutdowns</h2>
              </div>
            </div>
          </div>

        <div className="grid grid-cols-1 xl:grid-cols-[3fr_2fr] gap-6 items-start">
          <div className="cts-table-card">
            <table className="cts-table" {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => {
                      const sortState = column.isSorted
                        ? column.isSortedDesc
                          ? "desc"
                          : "asc"
                        : "none";
                      return (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps({
                              title: `Sort by ${column.render("Header")}`,
                            })
                          )}
                        >
                          <span className="inline-flex items-center">
                            {column.render("Header")}
                            <span className="cts-sort">
                              <SortIndicator state={sortState} />
                            </span>
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {paddedPage.map((row, idx) => {
                  if (!row) {
                    return (
                      <tr key={`empty-${idx}`} className="cts-empty-row">
                        <td colSpan={columns.length}>&nbsp;</td>
                      </tr>
                    );
                  }
                  prepareRow(row);
                  const isActive =
                    row.original.buildingId === activeBuildingId;
                  return (
                    <tr
                      onMouseEnter={() => handleMouseEnter(row.original)}
                      onClick={() => handleRowClick(row.original)}
                      {...row.getRowProps({
                        className: isActive ? "is-active" : "",
                      })}
                    >
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="cts-pagination">
              <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                {"«"}
              </button>
              <button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                {"‹"}
              </button>
              {Array.from({ length: 5 }, (_, i) => {
                const pageNumber = pageIndex + 1 + i;
                const isDisabled = pageNumber > pageOptions.length;
                return (
                  <button
                    key={pageNumber}
                    disabled={isDisabled}
                    onClick={() => !isDisabled && gotoPage(pageNumber - 1)}
                    style={
                      pageNumber - 1 === pageIndex
                        ? {
                            background: "rgba(187,218,98,0.2)",
                            borderColor: "#BBDA62",
                          }
                        : undefined
                    }
                  >
                    {pageNumber}
                  </button>
                );
              })}
              <button onClick={() => nextPage()} disabled={!canNextPage}>
                {"›"}
              </button>
              <button
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              >
                {"»"}
              </button>
            </div>
          </div>
          <AlertTrendsCard
            records={allAlertRecords}
            title="Organization Trends"
          />
        </div>
        </>
      )}
    </div>
  );
}
